// L0 — deterministic extraction for the workflow miner. Pure, no browser
// or agent dependencies, so it unit-tests in isolation (the agent stages
// in workflowMining.js can't — they pull in the in-browser LLM runtime).
//
// Job: turn a parsed api_request (parseApiRequest output) into a compact,
// readable "tool timeline + conversational spine" that a reader sub-agent
// can narrate, plus the entity ledger and the session canonicalization.
// This is the ONLY deterministic step left in the pipeline — everything
// semantic is done by the agents downstream.

export const READER_CHUNK_CHARS = 24000; // ~6k tokens of timeline per reader pass before we roll
const MAX_INPUT_CHARS = 400; // per tool-call input in the rendered timeline
const MAX_RESULT_CHARS = 400; // per tool-result content
const MAX_TEXT_CHARS = 600; // per user/assistant prose block

function compact(value, cap) {
  let s;
  try {
    s = typeof value === "string" ? value : JSON.stringify(value);
  } catch {
    s = String(value);
  }
  s = String(s).replace(/\s+/g, " ").trim();
  return s.length > cap ? `${s.slice(0, cap)}…` : s;
}

// A tool_result's content is a string OR an array of blocks (mirrors the
// helper in harnessTemplates). Pull the text so the reader sees the
// result, not a JSON envelope.
function resultText(content) {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .filter((b) => b && b.type === "text" && typeof b.text === "string")
      .map((b) => b.text)
      .join("\n");
  }
  return "";
}

// Render ONE parsed turn to compact timeline lines for the reader: the
// conversational spine (USER asks, ASSISTANT says) plus the tool
// timeline (CALL / RESULT), with thinking/images dropped and payloads
// truncated. Small enough to read at scale, faithful enough to narrate.
export function renderTurn(turn, { maxInput = MAX_INPUT_CHARS, maxResult = MAX_RESULT_CHARS } = {}) {
  const out = [];
  for (const b of turn?.blocks || []) {
    if (b.type === "text" && b.text && b.text.trim()) {
      // Label by role so the reader can tell harness setup (SYSTEM) from
      // the actual conversation — the system prompt carries useful memory
      // / project context, so we keep it rather than dropping it; the
      // machinery-vs-workflow call is the reader's (it's semantic, and
      // harness machinery also rides in USER turns).
      const who = turn.role === "user" ? "USER" : turn.role === "system" ? "SYSTEM" : "ASSISTANT";
      out.push(`${who}: ${compact(b.text, MAX_TEXT_CHARS)}`);
    } else if (b.type === "tool_use") {
      const args = b.input == null ? "" : compact(b.input, maxInput);
      out.push(`CALL ${b.name || "?"}(${args})`);
    } else if (b.type === "tool_result") {
      const tag = b.linkedToolName ? `[${b.linkedToolName}]` : "";
      const err = b.isError ? " (error)" : "";
      out.push(`RESULT${tag}${err}: ${compact(resultText(b.content), maxResult)}`);
    }
    // thinking / image / unknown — out of the workflow read
  }
  return out.join("\n");
}

// Split a parsed session into reader-sized chunks of rendered turns.
// Chunk on turn boundaries so a CALL never splits from its turn; pack
// greedily to the char budget. Most sessions yield ONE chunk
// (single-pass); long sessions roll across several with carried state.
export function chunkSessionTurns(turns, { budget = READER_CHUNK_CHARS, ...opts } = {}) {
  const chunks = [];
  let cur = [];
  let curLen = 0;
  for (const turn of turns || []) {
    const text = renderTurn(turn, opts);
    if (!text) continue;
    const len = text.length + 1;
    if (curLen + len > budget && cur.length) {
      chunks.push(cur.join("\n"));
      cur = [];
      curLen = 0;
    }
    cur.push(text);
    curLen += len;
  }
  if (cur.length) chunks.push(cur.join("\n"));
  return chunks.length ? chunks : [""];
}

// Append-only entity ledger — the files/commands/hosts/skills the session
// touched, unioned from the facets parseApiRequest already attached to
// each tool_use. Carried into every reader chunk so the rolling state
// can't silently forget an entity it saw early (the anti-drift assist).
export function extractFacets(turns) {
  const acc = { files: new Set(), commands: new Set(), hosts: new Set(), skills: new Set() };
  for (const turn of turns || []) {
    for (const b of turn?.blocks || []) {
      const f = b?.facets;
      if (!f) continue;
      for (const key of Object.keys(acc)) {
        for (const item of f[key] || []) {
          const label = typeof item === "string" ? item : item?.label;
          if (label) acc[key].add(label);
        }
      }
    }
  }
  return Object.fromEntries(Object.entries(acc).map(([k, v]) => [k, [...v].slice(0, 24)]));
}

// Normalize a label to a valid .skill frontmatter `name` so the skill
// draft's preview matches what shared-skill-format would accept: lowercase
// alphanumerics + hyphens, must start/end alphanumeric, 1–64 chars
// (NAME_RE = ^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$).
export function toSkillName(raw, fallback = "workflow-skill") {
  let s = String(raw || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (s.length > 64) s = s.slice(0, 64).replace(/-+$/g, "");
  return s || fallback;
}

// Newest-first scan window → one canonical session per id. Each
// api_request body carries the conversation so far, so the newest
// in-window prompt of a session is its fullest body. windowTraces is
// already newest-first, so first-seen per session is the one to read.
export function canonicalizeSessions(windowTraces) {
  const bySession = new Map();
  for (const t of windowTraces || []) {
    const sid = t.sessionId || t.id;
    if (!sid || bySession.has(sid)) continue;
    bySession.set(sid, { sessionId: sid, promptId: t.id, label: t.summary || "", timestamp: t.timestamp });
  }
  return [...bySession.values()];
}
